import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
  FlaskConical, Beaker, Play, Pause, RefreshCw, CheckCircle2, XCircle,
  AlertCircle, Bot, TrendingUp, TrendingDown, Zap,
} from "lucide-react";
import api from "../../lib/api";
import { Table, Td, StatusBadge } from "../../components/dashboard/Shared";

const fmt = (n, d = 2) => (typeof n === "number" ? n.toFixed(d) : n ?? "—");

// -------------------------------------------------
// Eligibility Debug Table
// -------------------------------------------------
export function EligibilityDebug({ onSetTestAccount }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/claude/dev/eligibility");
      setData(data);
    } catch { setData({ accounts: [] }); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="card-dark p-5" data-testid="eligibility-debug-card">
      <div className="flex items-center justify-between mb-3">
        <div>
          <p className="text-white font-display text-lg flex items-center gap-2">
            <AlertCircle size={16} /> Eligibility Debug
          </p>
          <p className="text-xs text-gray-500 mt-1">
            Every account with the exact reason it is/isn&apos;t eligible. Never silent.
            {data?.dev_mode && <span className="text-[#00F0FF] ml-1">· Dev Mode ON</span>}
          </p>
        </div>
        <button data-testid="eligibility-refresh" onClick={load} disabled={loading}
                className="btn-outline text-xs px-3 py-2 flex items-center gap-2 disabled:opacity-40">
          <RefreshCw size={12} className={loading ? "animate-spin" : ""} /> Refresh
        </button>
      </div>
      {!data ? <div className="skeleton h-24" /> : data.accounts.length === 0 ? (
        <p className="text-xs text-gray-500 py-6 text-center">No MT5 accounts connected yet.</p>
      ) : (
        <Table headers={["Client", "Login", "Connected", "Trading", "KYC", "AI Consent", "Admin", "Prod Reason", "Dev", "Action"]}>
          {data.accounts.map((a) => (
            <tr key={a.account_id} className="border-b border-white/5" data-testid={`elig-row-${a.login}`}>
              <Td className="text-xs">{a.client} <span className="text-gray-600">({a.role})</span></Td>
              <Td className="tabular text-xs text-gray-400">{a.broker} · {a.login}</Td>
              <Td>{a.connected ? <CheckCircle2 size={13} className="text-[#00F0FF]" /> : <XCircle size={13} className="text-[#FF3B30]" />}</Td>
              <Td>{a.trading_enabled ? <CheckCircle2 size={13} className="text-[#00F0FF]" /> : <XCircle size={13} className="text-[#FF3B30]" />}</Td>
              <Td className="text-xs">{a.kyc}</Td>
              <Td>{a.ai_consent ? <CheckCircle2 size={13} className="text-[#00F0FF]" /> : <XCircle size={13} className="text-[#FF3B30]" />}</Td>
              <Td className="text-xs">{a.admin_approval}</Td>
              <Td className={`text-[11px] !whitespace-normal max-w-xs ${a.prod_eligible ? "text-[#00F0FF]" : "text-amber-400"}`}>{a.prod_reason}</Td>
              <Td>{a.dev_eligible ? <CheckCircle2 size={13} className="text-[#00F0FF]" /> : <XCircle size={13} className="text-gray-500" />}</Td>
              <Td>
                <button data-testid={`set-test-${a.login}`} onClick={() => onSetTestAccount && onSetTestAccount(a.account_id, a.client)}
                        className="text-[10px] text-[#00F0FF] hover:underline">Set as Test</button>
              </Td>
            </tr>
          ))}
        </Table>
      )}
    </div>
  );
}

// -------------------------------------------------
// Experimental Mode Panel
// -------------------------------------------------
export function ExperimentalMode({ settings, onSaved }) {
  const [status, setStatus] = useState(null);
  const [analyses, setAnalyses] = useState([]);
  const [busy, setBusy] = useState(false);
  const [runningNow, setRunningNow] = useState(false);

  const load = useCallback(async () => {
    try {
      const [{ data: st }, { data: an }] = await Promise.all([
        api.get("/claude/dev/status"),
        api.get("/claude/dev/experimental/analyses?limit=10"),
      ]);
      setStatus(st); setAnalyses(an);
    } catch { /* keep prev */ }
  }, []);
  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [load]);

  const start = async () => {
    setBusy(true);
    try {
      await api.post("/claude/dev/experimental/start");
      toast.success("Experimental mode started — first cycle in ~5 min");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Start failed");
    } finally { setBusy(false); }
  };
  const stop = async () => {
    setBusy(true);
    try {
      await api.post("/claude/dev/experimental/stop");
      toast.success("Experimental mode stopped");
      await load();
    } catch { toast.error("Stop failed"); }
    finally { setBusy(false); }
  };
  const runNow = async () => {
    setRunningNow(true);
    try {
      const { data } = await api.post("/claude/dev/experimental/run-now");
      toast.success("Cycle complete — see result");
      console.log("experimental cycle:", data);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Run failed");
    } finally { setRunningNow(false); }
  };

  const last = status?.last_analysis;
  const dec = last?.decision;
  const enabled = !!status?.experimental_enabled;

  return (
    <div className="card-dark p-5 border border-[#7000FF]/30" data-testid="experimental-card">
      <div className="flex items-start justify-between mb-3">
        <div>
          <p className="text-white font-display text-lg flex items-center gap-2">
            <FlaskConical size={16} className="text-[#7000FF]" /> Claude Experimental Mode
            <span className="text-[10px] uppercase tracking-wider bg-[#7000FF]/20 text-[#7000FF] px-2 py-0.5 rounded">DEV ONLY</span>
          </p>
          <p className="text-xs text-gray-500 mt-1">
            Autonomous Claude decisions every 5 min — production Rule Engine untouched. Fixed 0.01 lot, max 1 open, max 5/day, 2% daily loss cap.
          </p>
          <p className="text-[10px] text-amber-400 mt-1">
            Test account: <span className="tabular">{status?.test_account_id ? status.test_account_id.slice(0, 12) + "…" : "not set"}</span>
            {!settings?.dev_mode && <span className="ml-3 text-[#FF3B30]">Requires dev_mode ON</span>}
          </p>
        </div>
        <div className="flex gap-2">
          <button data-testid="exp-run-now" onClick={runNow} disabled={runningNow || !enabled}
                  className="btn-outline text-xs px-3 py-2 flex items-center gap-2 disabled:opacity-40">
            <RefreshCw size={12} className={runningNow ? "animate-spin" : ""} /> Run now
          </button>
          {enabled ? (
            <button data-testid="exp-stop" onClick={stop} disabled={busy}
                    className="text-xs px-3 py-2 rounded flex items-center gap-2 bg-[#FF3B30]/15 border border-[#FF3B30]/40 text-[#FF3B30] disabled:opacity-40">
              <Pause size={12} /> STOP
            </button>
          ) : (
            <button data-testid="exp-start" onClick={start} disabled={busy || !settings?.dev_mode || !settings?.test_account_id}
                    className="btn-primary text-xs px-3 py-2 flex items-center gap-2 disabled:opacity-40">
              <Play size={12} /> Start
            </button>
          )}
        </div>
      </div>

      <div className="grid md:grid-cols-4 gap-3">
        <div className="p-3 rounded bg-white/5">
          <p className="label-xs">State</p>
          <p className={`text-lg font-display ${enabled ? "text-[#00F0FF]" : "text-gray-400"}`}>{enabled ? "RUNNING" : "STOPPED"}</p>
          <p className="text-[10px] text-gray-500 mt-0.5">Scheduler alive: {status?.scheduler_alive ? "yes" : "no"}</p>
        </div>
        <div className="p-3 rounded bg-white/5">
          <p className="label-xs">Trades today</p>
          <p className="text-lg font-display tabular">{status?.trades_today ?? 0} / 5</p>
        </div>
        <div className="p-3 rounded bg-white/5">
          <p className="label-xs">Last confidence</p>
          <p className="text-lg font-display tabular">{dec?.confidence ?? "—"}</p>
        </div>
        <div className="p-3 rounded bg-white/5">
          <p className="label-xs">Last decision</p>
          <p className={`text-lg font-display ${dec?.action === "BUY" ? "text-[#00F0FF]" : dec?.action === "SELL" ? "text-[#FF3B30]" : "text-gray-400"}`}>
            {dec?.action || "—"}
          </p>
        </div>
      </div>

      {last && (
        <div className="mt-4 p-3 rounded bg-white/5 space-y-2">
          <div className="flex items-center gap-2 text-xs">
            <span className="label-xs">Last analysis</span>
            <span className="text-gray-500">{new Date(last.ts).toLocaleString()}</span>
            <span className="ml-auto text-[10px] uppercase tracking-wider text-[#7000FF]">{last.stage}</span>
          </div>
          {last.snapshot_summary && (
            <p className="text-[11px] text-gray-400 tabular">
              Price ${fmt(last.snapshot_summary.price)} · Spread ${fmt(last.snapshot_summary.spread, 3)}
              · ATR ${fmt(last.snapshot_summary.atr)} · {last.snapshot_summary.session || "off-session"}
              · Trend {last.snapshot_summary.trend?.dominant} (agree {last.snapshot_summary.trend?.agree}/3)
            </p>
          )}
          {dec && (
            <div className="text-xs space-y-1">
              {dec.action !== "NO_TRADE" && (
                <p className="tabular">
                  Entry ${fmt(dec.entry)} · SL ${fmt(dec.sl)} · TP ${fmt(dec.tp)}
                </p>
              )}
              <p className="text-gray-300 italic">&ldquo;{dec.reasoning}&rdquo;</p>
            </div>
          )}
          {last.execution && (
            <div className="flex items-center gap-2 text-xs pt-1 border-t border-white/5">
              {last.execution.ok ? (
                <><CheckCircle2 size={12} className="text-[#00F0FF]" />
                  <span className="text-[#00F0FF]">Order {last.execution.order_id}</span></>
              ) : (
                <><XCircle size={12} className="text-[#FF3B30]" />
                  <span className="text-[#FF3B30]">{last.execution.error}</span></>
              )}
            </div>
          )}
        </div>
      )}

      {analyses.length > 0 && (
        <div className="mt-4">
          <p className="label-xs mb-2">Recent cycles</p>
          <Table headers={["Time", "Stage", "Action", "Conf", "Reasoning / Note"]}>
            {analyses.slice(0, 8).map((a) => (
              <tr key={a.ts} className="border-b border-white/5">
                <Td className="text-[11px] text-gray-500">{new Date(a.ts).toLocaleTimeString()}</Td>
                <Td className="text-[11px]">{a.stage}</Td>
                <Td className={a.decision?.action === "BUY" ? "text-[#00F0FF]" : a.decision?.action === "SELL" ? "text-[#FF3B30]" : "text-gray-400"}>
                  {a.decision?.action || "—"}
                </Td>
                <Td className="tabular text-xs">{a.decision?.confidence ?? "—"}</Td>
                <Td className="text-[11px] text-gray-500 !whitespace-normal max-w-md">
                  {a.decision?.reasoning || a.skipped || a.error || a.exception || "—"}
                </Td>
              </tr>
            ))}
          </Table>
        </div>
      )}
    </div>
  );
}


// -------------------------------------------------
// Direct Mode Panel — M1 candle cadence, Claude-only, full control
// -------------------------------------------------
export function DirectMode({ settings }) {
  const [status, setStatus] = useState(null);
  const [calls, setCalls] = useState([]);
  const [trades, setTrades] = useState([]);
  const [busy, setBusy] = useState(false);
  const [runningNow, setRunningNow] = useState(false);
  const [countdown, setCountdown] = useState(60);

  const load = useCallback(async () => {
    try {
      const [{ data: st }, { data: cs }, { data: tr }] = await Promise.all([
        api.get("/claude/dev/direct/status"),
        api.get("/claude/dev/direct/calls?limit=8"),
        api.get("/claude/dev/direct/trades?limit=8"),
      ]);
      setStatus(st); setCalls(cs); setTrades(tr);
    } catch { /* keep */ }
  }, []);
  useEffect(() => {
    load();
    const t = setInterval(load, 12000);
    return () => clearInterval(t);
  }, [load]);
  useEffect(() => {
    const t = setInterval(() => {
      setCountdown((c) => {
        const now = new Date();
        return 60 - now.getSeconds();
      });
    }, 1000);
    return () => clearInterval(t);
  }, []);

  const start = async () => {
    setBusy(true);
    try { await api.post("/claude/dev/direct/start"); toast.success("Direct Mode started — Claude fully in control"); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Start failed"); }
    finally { setBusy(false); }
  };
  const stop = async () => {
    setBusy(true);
    try { await api.post("/claude/dev/direct/stop"); toast.success("Direct Mode stopped"); await load(); }
    catch { toast.error("Stop failed"); }
    finally { setBusy(false); }
  };
  const runNow = async () => {
    setRunningNow(true);
    try {
      const { data } = await api.post("/claude/dev/direct/run-now");
      console.log("direct cycle:", data);
      toast.success(`Cycle: ${data?.result?.stage || data?.skipped || "done"}`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Run failed"); }
    finally { setRunningNow(false); }
  };

  const enabled = !!status?.direct_enabled;
  const call = status?.last_call;
  const trade = status?.last_trade;
  const cur = status?.current_trade;
  const decision = call?.response;

  return (
    <div className="card-dark p-5 border border-[#FF3B30]/30" data-testid="direct-mode-card">
      <div className="flex items-start justify-between mb-3">
        <div>
          <p className="text-white font-display text-lg flex items-center gap-2">
            <Zap size={16} className="text-[#FF3B30]" /> Claude Direct Mode
            <span className="text-[10px] uppercase tracking-wider bg-[#FF3B30]/20 text-[#FF3B30] px-2 py-0.5 rounded">DEV ONLY</span>
          </p>
          <p className="text-xs text-gray-500 mt-1">
            Zero rule filters. Claude is the ONLY decision maker. Every M1 candle close → snapshot to Claude → decision. Claude also manages open positions (HOLD / MOVE_SL / CLOSE_PARTIAL / CLOSE / REVERSE). Fixed 0.01 lot, max 1 open.
          </p>
        </div>
        <div className="flex gap-2">
          <button data-testid="direct-run-now" onClick={runNow} disabled={runningNow || !enabled}
                  className="btn-outline text-xs px-3 py-2 flex items-center gap-2 disabled:opacity-40">
            <RefreshCw size={12} className={runningNow ? "animate-spin" : ""} /> Run now
          </button>
          {enabled ? (
            <button data-testid="direct-stop" onClick={stop} disabled={busy}
                    className="text-xs px-3 py-2 rounded flex items-center gap-2 bg-[#FF3B30]/15 border border-[#FF3B30]/40 text-[#FF3B30] disabled:opacity-40">
              <Pause size={12} /> STOP
            </button>
          ) : (
            <button data-testid="direct-start" onClick={start}
                    disabled={busy || !settings?.dev_mode || !settings?.test_account_id}
                    className="btn-primary text-xs px-3 py-2 flex items-center gap-2 disabled:opacity-40">
              <Play size={12} /> Start
            </button>
          )}
        </div>
      </div>

      <div className="grid md:grid-cols-6 gap-3">
        <div className="p-3 rounded bg-white/5">
          <p className="label-xs">State</p>
          <p className={`text-lg font-display ${enabled ? "text-[#00F0FF]" : "text-gray-400"}`}>{enabled ? "LIVE" : "STOPPED"}</p>
        </div>
        <div className="p-3 rounded bg-white/5">
          <p className="label-xs">Next candle</p>
          <p className="text-lg font-display tabular text-[#00F0FF]">{Math.max(0, countdown)}s</p>
        </div>
        <div className="p-3 rounded bg-white/5">
          <p className="label-xs">Claude calls today</p>
          <p className="text-lg font-display tabular">{status?.calls_today ?? 0}</p>
          <p className="text-[10px] text-gray-500 mt-0.5">avg {status?.avg_latency_ms ?? 0}ms</p>
        </div>
        <div className="p-3 rounded bg-white/5">
          <p className="label-xs">Cost today</p>
          <p className="text-lg font-display tabular">${fmt(status?.cost_today_usd, 4)}</p>
        </div>
        <div className="p-3 rounded bg-white/5">
          <p className="label-xs">Trades / Win rate</p>
          <p className="text-lg font-display tabular">{status?.trades_today ?? 0}</p>
          <p className="text-[10px] text-gray-500 mt-0.5">{status?.win_rate ?? 0}%</p>
        </div>
        <div className="p-3 rounded bg-white/5">
          <p className="label-xs">Current trade</p>
          <p className={`text-sm ${cur?.direction === "BUY" ? "text-[#00F0FF]" : cur?.direction === "SELL" ? "text-[#FF3B30]" : "text-gray-400"}`}>
            {cur ? `${cur.direction} @ SL$${fmt(cur.sl)} TP$${fmt(cur.tp)}` : "—"}
          </p>
        </div>
      </div>

      {call && (
        <div className="mt-4 p-3 rounded bg-white/5 space-y-2">
          <div className="flex items-center gap-2 text-xs">
            <span className="label-xs">Last Claude decision · {call.kind}</span>
            <span className="text-gray-500">{new Date(call.ts).toLocaleTimeString()}</span>
            <span className="ml-auto text-[10px] text-gray-500">{call.latency_ms}ms · ${fmt(call.cost_usd, 4)} · {call.input_tokens}/{call.output_tokens} tok</span>
          </div>
          {decision && (
            <div className="text-xs">
              <p className={decision.action === "BUY" ? "text-[#00F0FF]" : decision.action === "SELL" ? "text-[#FF3B30]" : "text-gray-300"}>
                {decision.action}
                {decision.entry && ` · Entry ${decision.entry}`}
                {decision.sl && ` · SL ${decision.sl}`}
                {decision.tp && ` · TP ${decision.tp}`}
                {decision.new_sl && ` · new SL ${decision.new_sl}`}
                {decision.confidence != null && ` · Conf ${decision.confidence}`}
                {decision.expected_hold_min && ` · hold ~${decision.expected_hold_min}m`}
              </p>
              {decision.reasoning && <p className="text-gray-400 italic mt-1">&ldquo;{decision.reasoning}&rdquo;</p>}
            </div>
          )}
          {call.error && <p className="text-[#FF3B30] text-xs">Error: {call.error}</p>}
        </div>
      )}

      {trades.length > 0 && (
        <div className="mt-4">
          <p className="label-xs mb-2">Recent trades</p>
          <Table headers={["Opened", "Dir", "SL/TP", "Status", "PnL", "Exit"]}>
            {trades.slice(0, 8).map((t) => (
              <tr key={t.trade_id} className="border-b border-white/5">
                <Td className="text-[11px] text-gray-500">{new Date(t.ts_open).toLocaleTimeString()}</Td>
                <Td className={t.direction === "BUY" ? "text-[#00F0FF]" : "text-[#FF3B30]"}>{t.direction}</Td>
                <Td className="tabular text-[11px]">${fmt(t.sl)} / ${fmt(t.tp)}</Td>
                <Td className="text-[11px]">{t.status}</Td>
                <Td className={`tabular text-[11px] ${(t.pnl_usd || 0) > 0 ? "text-[#00F0FF]" : (t.pnl_usd || 0) < 0 ? "text-[#FF3B30]" : ""}`}>
                  {t.pnl_usd != null ? `$${fmt(t.pnl_usd)}` : "—"}
                </Td>
                <Td className="text-[11px] text-gray-500">{t.exit_reason || "—"}</Td>
              </tr>
            ))}
          </Table>
        </div>
      )}
    </div>
  );
}
